/**
 * Segment Adapter wizard for /admin/conversion-roi.
 *
 * Unlike PostHog/Mixpanel/Amplitude, Segment is webhook-based — we mint a
 * URL + HMAC secret, the operator paste them in their Segment workspace as
 * a Webhook destination, events flow IN to us.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Plug, CheckCircle2, Trash2, ShieldCheck, Copy, RefreshCw } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function SegmentAdapterWizard() {
  const [config, setConfig] = useState(null);
  const [minting, setMinting] = useState(false);
  const [mintResult, setMintResult] = useState(null);
  const [copied, setCopied] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`${API}/api/segment-adapter/config`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const d = await r.json();
      setConfig(d.connected ? d : null);
    } catch { /* ignore */ }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  const mint = async () => {
    if (config && !window.confirm('A webhook URL already exists. Minting again will ROTATE the HMAC secret — your existing Segment destination will start failing until you update it. Continue?')) return;
    setMinting(true);
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`${API}/api/segment-adapter/mint`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      const d = await r.json();
      if (r.ok) {
        setMintResult(d);
        refresh();
      } else setMintResult({ error: d.detail || `HTTP ${r.status}` });
    } finally { setMinting(false); }
  };

  const disconnect = async () => {
    if (!window.confirm('Disable Segment webhook? Already-imported conversion_events are kept; incoming events will be rejected.')) return;
    const token = localStorage.getItem('token');
    await fetch(`${API}/api/segment-adapter/config`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
    setMintResult(null);
    refresh();
  };

  const copy = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      setTimeout(() => setCopied(null), 1500);
    } catch { /* blocked */ }
  };

  const webhookUrl = mintResult?.tenant_id
    ? `${API}/api/segment-adapter/webhook?tenant_id=${mintResult.tenant_id}`
    : config?.tenant_id
      ? `${API}/api/segment-adapter/webhook?tenant_id=${config.tenant_id}`
      : null;

  return (
    <Card className="bg-slate-950 border-emerald-500/30" data-testid="segment-adapter-wizard">
      <CardHeader>
        <CardTitle className="text-base text-white flex items-center gap-2">
          <Plug className="w-4 h-4 text-emerald-400" />
          Segment adapter — passive webhook (events stream IN)
          <Badge className="bg-emerald-500/20 text-emerald-200 border border-emerald-400/40 text-[9px] font-mono tracking-widest ml-1">P1</Badge>
        </CardTitle>
        <p className="text-sm text-slate-200 mt-1">Add CARTIE as a Segment <b className="text-white">Webhook destination</b> — every event Segment routes flows into CARTIE. HMAC-SHA256 verified. No polling, no API key juggling.</p>
      </CardHeader>
      <CardContent className="space-y-4">
        {config ? (
          <div className="rounded-md bg-slate-900 border border-emerald-500/30 p-3">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="text-sm">
                <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                  <ShieldCheck className="w-4 h-4 text-emerald-300" />
                  <span className="text-white font-bold">Webhook active</span>
                </div>
                <div className="text-xs text-slate-300">
                  Received: <span className="text-emerald-300 font-bold">{config.received_count || 0}</span> events
                  {config.unmapped_count > 0 && <> · <span className="text-amber-300">{config.unmapped_count}</span> unmapped</>}
                </div>
                {config.last_received_at && (
                  <div className="text-xs text-slate-300 mt-0.5">
                    Last event: {new Date(config.last_received_at).toLocaleString()}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Button size="sm" onClick={mint} disabled={minting} className="bg-amber-600 hover:bg-amber-500 text-white h-8 text-xs" data-testid="segment-rotate-btn">
                  <RefreshCw className="w-3 h-3 mr-1" /> Rotate secret
                </Button>
                <Button size="sm" variant="ghost" onClick={disconnect} className="text-rose-300 hover:text-rose-200 h-8 px-2">
                  <Trash2 className="w-3 h-3" />
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <div className="rounded-md bg-slate-900 border border-emerald-500/20 p-3 text-center">
            <div className="text-sm text-slate-200 mb-3">No webhook minted yet. Click below to generate a URL + HMAC secret.</div>
            <Button onClick={mint} disabled={minting} className="bg-emerald-600 hover:bg-emerald-500 text-white" data-testid="segment-mint-btn">
              {minting ? 'Minting…' : 'Mint webhook URL'}
            </Button>
          </div>
        )}

        {mintResult && !mintResult.error && (
          <div className="rounded-md bg-slate-900 border border-emerald-500/30 p-3 space-y-2.5" data-testid="segment-mint-result">
            <div className="text-xs font-bold text-emerald-300 uppercase tracking-widest">📋 Paste these in Segment</div>

            <div>
              <div className="text-[10px] font-bold text-slate-300 mb-0.5 uppercase tracking-widest">Webhook URL</div>
              <div className="flex items-center gap-2">
                <code className="flex-1 text-[11px] font-mono text-emerald-300 bg-slate-950 border border-emerald-500/20 rounded px-2 py-1.5 break-all" data-testid="segment-webhook-url">{webhookUrl}</code>
                <Button size="sm" variant="outline" onClick={() => copy(webhookUrl, 'url')} className="text-xs h-7 border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10">
                  {copied === 'url' ? <><CheckCircle2 className="w-3 h-3 mr-1" /> Copied</> : <><Copy className="w-3 h-3 mr-1" /> Copy</>}
                </Button>
              </div>
            </div>

            <div>
              <div className="text-[10px] font-bold text-slate-300 mb-0.5 uppercase tracking-widest">Shared Secret (HMAC-SHA256)</div>
              <div className="flex items-center gap-2">
                <code className="flex-1 text-[11px] font-mono text-emerald-300 bg-slate-950 border border-emerald-500/20 rounded px-2 py-1.5 break-all" data-testid="segment-hmac-secret">{mintResult.hmac_secret}</code>
                <Button size="sm" variant="outline" onClick={() => copy(mintResult.hmac_secret, 'secret')} className="text-xs h-7 border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10">
                  {copied === 'secret' ? <><CheckCircle2 className="w-3 h-3 mr-1" /> Copied</> : <><Copy className="w-3 h-3 mr-1" /> Copy</>}
                </Button>
              </div>
            </div>

            <details className="text-xs text-slate-200 cursor-pointer">
              <summary className="text-emerald-300 font-bold">Setup steps ▾</summary>
              <ol className="mt-2 space-y-1 ml-4 list-decimal">
                {(mintResult.instructions || []).map((s, i) => <li key={i} className="text-slate-200">{s}</li>)}
              </ol>
            </details>

            <div className="text-[10px] text-amber-300 italic">
              ⚠ Store the secret now — it cannot be retrieved later. To rotate, click "Rotate secret" (your Segment destination will fail until you update it).
            </div>
          </div>
        )}
        {mintResult?.error && (
          <div className="text-xs text-rose-300">{mintResult.error}</div>
        )}
      </CardContent>
    </Card>
  );
}
